import { UseMutateFunction } from '@tanstack/react-query'
import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@app/context/ModalsContext'
import { useSnackbar } from '@app/context/SnackbarContext'

import { AuthSessionContext } from '@context/AuthContext'

import { getDateTimeFormat } from '@ui/v2/Table/cells/DateTime'
import { useTableColumns } from '@ui/v2/Table/hooks/useTableRows'
import { TableViewMoreModal } from '@ui/v2/Table/modals/TableViewMore.modal'

import { useWindowResize } from '@utils/domUtils'

import { ExportHubDto } from '@model/ExportHub'

import { IAppendableFetchQueryResult, SearchResponseProps } from '../IBReportResultsPage'
import { IBReportsTable } from '../IBReportsTable'
import { ExportHubColumn, ExportHubColumnCtx, exportHubColumns } from './ExportHubTable.columns'

export type ExportHubTableSortKeys = 'ReportName' | 'Section' | 'CreatedDate' | 'Format' | 'Status'

interface ExportHubTableProps {
  data: IAppendableFetchQueryResult<ExportHubDto> & {
    retryReport: UseMutateFunction<void, Error, string, unknown>
    isRetryPending: boolean
  }
  search: SearchResponseProps
}

export const ExportHubTable: React.FC<ExportHubTableProps> = (props) => {
  const { data: exportHub, search } = props
  const { query, retryReport, isRetryPending } = exportHub

  const { addSnackbar } = useSnackbar()
  const isMobile = useWindowResize()
  const [auth] = useContext(AuthSessionContext)

  const rawDateFormat = auth?.dateFormatType?.name
  const dateFormat = getDateTimeFormat(rawDateFormat)

  const { t } = useTranslation()
  const colsFormatCtx: ExportHubColumnCtx = {
    t,
    dateFormat,
    retryReport,
    isRetryPending: isRetryPending,
    addSnackbar,
    isMobile,
  }

  const data = exportHub.query.data ?? []
  const reportViewMoreModal = useModal(TableViewMoreModal<ExportHubDto>, () => ({
    data: data,
    formatCtx: colsFormatCtx,
    columns: exportHubColumns,
  }))

  const { renderColumns } = useTableColumns<ExportHubDto, ExportHubColumnCtx, ExportHubColumn>({
    columns: exportHubColumns,
    formatCtx: colsFormatCtx,
    onArrowClick: (_, index) => reportViewMoreModal.open({ initialIndex: index }),
  })

  useEffect(() => {
    if (reportViewMoreModal.isOpen) {
      reportViewMoreModal.reRender()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, reportViewMoreModal.isOpen, reportViewMoreModal.reRender])

  return (
    <IBReportsTable
      tableQuery={query}
      sortOptions={search.sortOptions}
      renderColumns={renderColumns}
    />
  )
}
